'use client'

import { type ComponentProps, type ComponentPropsWithoutRef, forwardRef, type MouseEventHandler, useId } from 'react'
import clsx from 'clsx'
import { RadioIndicatorIcon } from '@/features/priceCalculator/PurchaseForm/OfferPresenter/CardRadioGroup/RadioIndicatorIcon/RadioIndicatorIcon'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { Text } from 'ui/src/components/Text/Text'
import { useHighlightAnimation } from 'ui/src/utils/useHighlightAnimation'
import {
  card,
  cardLabel,
  item,
  itemRadioButton,
  labelledInputRadioItem,
  radioGroup,
  unlabeledInputRadioItem,
} from './InputRadio.css'

type Option = { label: string; value: string; autoFocus?: boolean }

type InputRadioProps = {
  label: string
  options: Array<Option>
  size?: 'small' | 'medium' | 'large'
  displayLabel?: boolean
} & Omit<ComponentProps<typeof RadioGroup.Root>, 'children'>

export function InputRadio({ displayLabel = true, orientation = 'horizontal', ...forwardedProps }: InputRadioProps) {
  if (displayLabel) {
    return <LabelledInputRadio orientation={orientation} {...forwardedProps} />
  }

  return <UnlabelledInputRadio orientation={orientation} {...forwardedProps} />
}

function LabelledInputRadio({
  label,
  options,
  onValueChange,
  size = 'medium',
  orientation,
  ...forwardedProps
}: Omit<InputRadioProps, 'displayLabel'> & Required<Pick<InputRadioProps, 'orientation'>>) {
  const { highlight, animationProps } = useHighlightAnimation<HTMLDivElement>()

  const handleValueChange = (value: string) => {
    highlight()
    onValueChange?.(value)
  }

  return (
    <div className={card[size]} data-orientation={orientation} {...animationProps}>
      <Text as="p" className={cardLabel[size]} size="xs" color="textSecondary" data-orientation={orientation}>
        {label}
      </Text>
      <RadioGroup.Root
        {...forwardedProps}
        className={radioGroup.withLabel}
        aria-label={label}
        orientation={orientation}
        data-orientation={orientation}
        onValueChange={handleValueChange}
      >
        {options.map((option) => {
          return <Item key={option.value} className={labelledInputRadioItem[size]} size={size} {...option} />
        })}
      </RadioGroup.Root>
    </div>
  )
}

function UnlabelledInputRadio({
  label,
  options,
  size = 'medium',
  orientation,
  ...forwardedProps
}: Omit<InputRadioProps, 'displayLabel'> & Required<Pick<InputRadioProps, 'orientation'>>) {
  return (
    <RadioGroup.Root
      {...forwardedProps}
      className={radioGroup.withoutLabel}
      aria-label={label}
      orientation={orientation}
      data-orientation={orientation}
    >
      {options.map((option) => {
        return (
          <HighlightableItem key={option.value} className={unlabeledInputRadioItem[size]} size={size} {...option} />
        )
      })}
    </RadioGroup.Root>
  )
}

type ItemProps = {
  label: string
  value: string
  size?: 'small' | 'medium' | 'large'
} & Omit<ComponentPropsWithoutRef<'button'>, 'value'>

const Item = forwardRef<HTMLLabelElement, ItemProps>(
  ({ value, label, size = 'medium', className, ...itemProps }, ref) => {
    const identifier = useId()

    return (
      <label ref={ref} className={clsx(item[size], className)} htmlFor={identifier}>
        <RadioGroup.Item id={identifier} className={itemRadioButton} value={value} {...itemProps}>
          <RadioGroup.Indicator forceMount={true} asChild={true}>
            <RadioIndicatorIcon />
          </RadioGroup.Indicator>
        </RadioGroup.Item>
        <span>{label}</span>
      </label>
    )
  },
)
Item.displayName = 'Item'

function HighlightableItem(props: ItemProps) {
  const { highlight, animationProps } = useHighlightAnimation<HTMLLabelElement>()

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    highlight()
    props.onClick?.(event)
  }

  return <Item {...props} {...animationProps} onClick={handleClick} />
}
